import { Controller } from "stimulus";

const DARK_TEXT = "#1d1d2b";
const LIGHT_TEXT = "#ffffff";
const HEX_REGEXP = /^#?[0-9a-fA-F]{6}$/;

const rgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const luminance = (hex) => {
  const [r, g, b] = rgb(hex).map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const textFor = (hex) =>
  contrast(hex, LIGHT_TEXT) >= contrast(hex, DARK_TEXT) ? LIGHT_TEXT : DARK_TEXT;

const normalize = (value) => {
  const v = (value || "").trim();
  if (!HEX_REGEXP.test(v)) return null;
  return (v[0] === "#" ? v : `#${v}`).toUpperCase();
};

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "editButton",
    "suggested",
    "swatches",
    "name",
    "customButton",
    "customPanel",
    "nativeColor",
    "nativeSwatch",
    "hexField",
  ];

  static values = {
    palette: Array,
  };

  connect() {
    this.color =
      normalize(this.inputTarget.value) || this.paletteValue[0].hex.toUpperCase();
    this.isCustom = !this.findSwatch(this.color);
    this.editing = false;
    this.panelOpen = false;
    this.startHex = null;
    this.buildSwatches();
    this.render();
    if (this.inputTarget.value.toUpperCase() !== this.color) {
      this.inputTarget.value = this.color;
    }
  }

  findSwatch(hex) {
    return this.paletteValue.find((p) => p.hex.toUpperCase() === hex);
  }

  buildSwatches() {
    this.swatchesTarget.querySelectorAll("[data-swatch]").forEach((el) => el.remove());
    const before = this.customButtonTarget;
    this.paletteValue.forEach((p) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "scp-swatch";
      button.dataset.swatch = p.hex.toUpperCase();
      button.dataset.action = "click->suggested-color-picker#pick keydown->suggested-color-picker#keydown";
      button.setAttribute("role", "radio");
      button.setAttribute("aria-label", p.name);
      button.title = p.name;
      button.style.background = p.hex;
      button.style.color = textFor(p.hex);
      button.innerHTML = '<i class="fa-solid fa-check"></i>';
      this.swatchesTarget.insertBefore(button, before);
    });
  }

  toggleEdit() {
    this.editing = !this.editing;
    this.panelOpen = this.editing && this.isCustom;
    this.render();
  }

  doneCustom() {
    this.panelOpen = false;
    if (this.color !== this.startHex) this.editing = false;
    this.render();
  }

  closeEditor() {
    this.editing = false;
    this.panelOpen = false;
  }

  pick(event) {
    this.isCustom = false;
    this.closeEditor();
    this.setColor(event.currentTarget.dataset.swatch);
  }

  openCustom() {
    this.isCustom = true;
    this.panelOpen = true;
    this.startHex = this.color;
    this.render();
    this.hexFieldTarget.focus();
  }

  keydown(event) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!keys[event.key]) return;
    event.preventDefault();
    const buttons = this.radioButtons();
    const index = buttons.indexOf(event.currentTarget);
    const next = buttons[(index + keys[event.key] + buttons.length) % buttons.length];
    next.focus();
    next.click();
  }

  radioButtons() {
    return Array.from(this.swatchesTarget.querySelectorAll('[role="radio"]'));
  }

  nativeInput(event) {
    event.stopPropagation();
    const hex = normalize(event.target.value);
    if (hex) this.setColor(hex, { notify: event.type === "change" });
  }

  nativeChange(event) {
    event.stopPropagation();
    const hex = normalize(event.target.value);
    if (hex) this.setColor(hex);
  }

  hexChange(event) {
    event.stopPropagation();
    const hex = normalize(event.target.value);
    if (hex) {
      this.setColor(hex);
    } else {
      this.hexFieldTarget.value = this.color;
    }
  }

  hexInput(event) {
    event.stopPropagation();
    const hex = normalize(event.target.value);
    if (hex && hex !== this.color) this.setColor(hex);
  }

  hexKeyup(event) {
    event.stopPropagation();
  }

  setColor(hex, { notify = true } = {}) {
    this.color = hex;
    this.render();
    if (this.inputTarget.value.toUpperCase() !== hex) {
      this.inputTarget.value = hex;
      if (notify) {
        this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  render() {
    const hex = this.color;
    const fg = textFor(hex);
    const swatch = this.findSwatch(hex);
    const isCustom = this.isCustom;

    this.previewTarget.style.background = hex;
    this.previewTarget.style.color = fg;
    this.previewTarget.style.setProperty("--scp-color", hex);

    this.nameTarget.textContent = isCustom ? `Custom · ${hex}` : swatch.name;

    this.radioButtons().forEach((button) => {
      const selected = !isCustom && button.dataset.swatch === hex;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-checked", selected ? "true" : "false");
      button.tabIndex = selected || (isCustom && button === this.customButtonTarget) ? 0 : -1;
      if (button.dataset.swatch) {
        button.style.boxShadow = selected ? `0 0 0 2px #fff, 0 0 0 4px ${button.dataset.swatch}` : "";
      }
    });

    const inner = this.customButtonTarget.querySelector(".scp-custom-inner");
    const icon = inner.querySelector("i");
    this.customButtonTarget.classList.toggle("is-selected", isCustom);
    inner.style.background = isCustom ? hex : "#fff";
    icon.className = isCustom ? "fa-solid fa-check" : "fa-solid fa-plus";
    icon.style.color = isCustom ? fg : "";

    const showPanel = this.editing && isCustom && this.panelOpen;
    this.suggestedTarget.hidden = !this.editing;
    this.customPanelTarget.hidden = !showPanel;
    this.editButtonTarget.classList.toggle("is-editing", this.editing);
    this.editButtonTarget.setAttribute("aria-expanded", this.editing ? "true" : "false");
    this.editButtonTarget.querySelector("i").className = this.editing ? "fa-solid fa-xmark" : "fa-solid fa-palette";
    this.editButtonTarget.querySelector("span").textContent = this.editing ? "Close" : "Change";
    this.nativeSwatchTarget.style.background = hex;
    this.nativeColorTarget.value = hex.toLowerCase();
    if (document.activeElement !== this.hexFieldTarget) {
      this.hexFieldTarget.value = hex;
    }
  }
}
